import { ASSESSMENT_CONCHECKLIST, ASSESSMENT_CONTRACT, ASSESSMENT_DILIGENCE, ASSESSMENT_INTAKE, ASSESSMENT_REVIEW, ASSESSMENT_TO_LOCK_TYPE, ASSESSMENT_VENDORVALUE, CRITICALITY_QUESTION_GUID, NOTIFY_MANUAL, NOTIFY_NONE, SURVEY_COMPLETE, SURVEY_OPEN, SURVEY_REVIEW } from './constants';
import { historyTables } from './history';
import { loadVendorEdit } from './main';
import { ajaxPromise, confirmDialog, displayNotification, htmlEsc, logerror, post, setTimeoutPromise } from './utils';
import { RecordLock, toggleAssessmentEditing } from './locking';

type AssessmentResponse = {
	resp_guid: string;
	resp_id: string | null;
	vend_id: string;
	cat_id: string;
	con_guid?: string;
	type: string;
	status: number;
	reviewedby: string;
	date: string;
	return_to_text: string;
	is_external: boolean;
	is_inline: boolean;
	show_submit: boolean;
	is_reviewer: boolean;
	cat_review: number;
	default_critical: string;
	lock_heartbeat_minutes: number;
	lock_timeout_minutes: number;
	lock_record_enabled: number;
	lock_banner_refresh_seconds: number;
	review: string;
	answers: any[];
};

export const collectAnswersFromForm = ($main: $, responseData: AssessmentResponse) => {
	const answers = [];
	const errors = [];

	$main.find('#response_form [data-questionid]').each((_, element) => {
		const $question = $(element);
		if (parseInt($question.data('hidden')) === 1) return;

		// Check if this is a text/textarea input (for intake forms)
		const $textInput = $question.find('.answer_text_value');
		let value, text;

		if ($textInput.length > 0) {
			// Text or textarea answer type
			text = $textInput.val()?.toString().trim() || '';
			value = 0; // No point value for text answers

			if (text === '') return; // Skip if empty
		} else {
			// Regular select answer type
			value = $question.find('select.answer_value').val() != null && $question.find('select.answer_value').val() != '' ? $question.find('select.answer_value').val() : '';
			text = $question.find('select.answer_value > :selected').length == 1 ? $question.find('select.answer_value > :selected').text() : '';

			const importance = $question.find('select.answer_importance').val() != null && $question.find('select.answer_importance').val() != '' ? parseFloat($question.find('select.answer_importance').val().toString()) : '';

			if (value == '' && importance == '') return;
		}

		const ans: Record<string, any> = {
			guid: $question.data('questionid'),
			value,
			text,
			comment: $question.find('textarea.answer_comment').length == 1 ? $question.find('textarea.answer_comment').val() : '',
		};

		// Include Importance if in Vendor Value
		if (parseInt(responseData.type) == ASSESSMENT_VENDORVALUE) {
			const importance = $question.find('select.answer_importance').val() != null && $question.find('select.answer_importance').val() != '' ? parseFloat($question.find('select.answer_importance').val().toString()) : '';
			ans.importance = importance;
		}

		// Check the other required fields, assuming value is set
		if ($textInput.length === 0) {
			// Standard validation for select-type answers
			if (ans.guid == '' || ans.text == '' || ans.value == '' || (parseInt(responseData.type) == ASSESSMENT_VENDORVALUE && ans.text != 'N/A' && ans.importance == '')) {
				errors.push($question);
				$question.find('select').parent().addClass('has-error');
				return;
			}
		} else {
			// Text input validation - just check text is present
			if (ans.guid == '' || ans.text == '') {
				errors.push($question);
				$question.find('.answer_text_value').parent().addClass('has-error');
				return;
			}
		}

		// All set!
		answers.push(ans);
	});

	return { answers, errors };
};

// simplified save progress for when lock release is triggered
export const saveProgressBackground = async ($main: $, responseData: AssessmentResponse, historyTable?: DataTables.Api | null): Promise<void> => {
	try {
		const { answers } = collectAnswersFromForm($main, responseData);

		if (!answers.length) return;

		let data = JSON.parse(JSON.stringify(responseData));
		data.answers = answers;
		data.reviewedby = $main.find('#response_form_reviewedby').val() || responseData.reviewedby;
		data.date = $main.find('#response_form_date').val();
		data.complete = 0; // Never mark complete on background save
		data.task_submit = 0;

		// Include vendor name for intake forms
		if ($main.find('#intake_vendor_name').length) {
			const vendorName = ($main.find('#intake_vendor_name').val() as string)?.trim() || null;
			data.vendor_name = vendorName ? vendorName : null;
		}

		// Include reviewers for intake forms
		if ($main.find('#intake_reviewers').length) {
			data.intake_reviewers = $main.find('#intake_reviewers').val() || [];
		}

		const res = await ajaxPromise('/form/submit', {
			type: 'response_save',
			data,
		});
		if (res.rc !== 'OK') throw res;

		if (historyTable) historyTable.ajax.reload();

	} catch (error) {
		logerror('assessment background save error', error);
	}
};

export const refreshAssessmentData = async ({ $main, response }: { $main: $, response: AssessmentResponse }) => {
	try {
		await setTimeoutPromise(500);

		const type = +response.type;

		const freshData = await ajaxPromise('/data/response_get', { data: response.resp_guid });

		if (freshData.rc !== 'OK') throw freshData;

		if (freshData.info.surveyresp_reviewedby !== response.reviewedby) {
			response.reviewedby = freshData.info.surveyresp_reviewedby;
			const $reviewedBySelect = $main.find('#response_form_reviewedby');

			// Check if the reviewedby value exists as an option in the dropdown
			const optionExists = $reviewedBySelect.find(`option[value="${response.reviewedby}"]`).length > 0;

			if (optionExists) {
				$reviewedBySelect.val(response.reviewedby).trigger('chosen:updated');
			} else {
				// If the reviewedby ID is not in the dropdown options, default to current user (value 0)
				$reviewedBySelect.val('0').trigger('chosen:updated');
				response.reviewedby = '0';
			}
		}
		if (freshData.info.surveyresp_date !== response.date) {
			response.date = freshData.info.surveyresp_date_nice;
			$main.find('#response_form_date').val(response.date).trigger('chosen:updated');
		}
		if (freshData.answers) {
			const freshAnswers = freshData.answers;
			response.answers = freshAnswers;
			$main.find('#response_form [data-questionid]').each((_, element) => {
				const $question = $(element);
				const questionId = $question.data('questionid');
				const freshAnswer = freshAnswers[questionId];

				if (freshAnswer) {
					const $select = $question.find('select.answer_value');
					if ($select.length) {
						$select.val(freshAnswer.surveyans_value).trigger('chosen:updated');
					}

					const $comment = $question.find('.answer_comment');
					if ($comment.length && freshAnswer.surveyans_comment) {
						$comment.val(freshAnswer.surveyans_comment);
					}

					const $importance = $question.find('.answer_importance');
					if ($importance.length && freshAnswer.surveyans_importance) {
						$importance.val(freshAnswer.surveyans_importance).trigger('chosen:updated');
					}
				}
			});
			// Refresh any section scoring if needed
			$main.find('.response_section').each((_, element) => {
				checkSectionScoring($(element), type);
			});
		}
	} catch (error) {
		logerror('Failed to refresh assessment data', error);
		displayNotification('Refresh Error', 'Failed to refresh assessment data', 'warning');
	}
};

export const checkSectionScoring = async ($section: $, type: number) => {
	const sectionScoring = [ASSESSMENT_DILIGENCE, ASSESSMENT_REVIEW, ASSESSMENT_CONTRACT].includes(type);
	if (!sectionScoring) return;
	const $questions = $section.find('.response_question').filter((_, element) => !+$(element).data('hidden'));
	const questions = [];
	const answers = {};
	$questions.each((_, element) => {
		const $question = $(element);
		questions.push({ surveyq_guid: $question.data('questionid') });
		const value = $question.find('select.answer_value').val();
		if (value) {
			const text = $question.find('select.answer_value > :selected').text();
			const surveyq_guid = $question.data('questionid');
			answers[surveyq_guid] = { surveyans_value: value, surveyans_text: text };
		}
	});
	const postData = {
		type,
		questions,
		answers,
	};
	const { scoring } = await ajaxPromise('/data/response_section_scoring', postData);
	$section.find('.response_basis').text(scoring.basis);
	$section.find('.response_rating').text(scoring.rating);
	$section.find('.response_percent').text(scoring.percent_pretty);
	$section.find('.response_score').text(scoring.score_pretty);
};

const initResponse = async ($main: $) => {

	let historyTable: DataTables.Api | null = null;

	const saveProgress = async ({ taskSubmit = false } = {}) => {
		let data = JSON.parse(JSON.stringify(response)); // Clone the response data
		$main.find('#response_form [data-questionid]').find('select').parent().removeClass('has-error'); // Reset the error state

		const { answers, errors } = collectAnswersFromForm($main, data);

		if (!errors.length && !answers.length) {
			//No answers (good or bad)
			displayNotification('Assessment', 'Please fill out an answer.', 'danger');
			return;
		}

		if (errors.length) {
			displayNotification('Assessment', 'Please fill out each response defined.', 'danger');
			return;
		}

		data.reviewedby = $main.find('#response_form_reviewedby').val() || response.reviewedby;
		data.date = $main.find('#response_form_date').val();
		data.complete = checkComplete() ? 1 : 0;
		data.task_submit = taskSubmit ? 1 : 0;
		data.answers = answers;

		// Include vendor name for intake forms
		if ($main.find('#intake_vendor_name').length) {
			const vendorName = ($main.find('#intake_vendor_name').val() as string)?.trim() || null;
			data.vendor_name = vendorName ? vendorName : null;
		}

		// Include reviewers for intake forms
		const reviewers = $main.find('#response_form_reviewers').val() || [];
		if (isIntake && reviewers.length) {
			data.intake_reviewers = reviewers;
		}

		if (isExternal && taskSubmit) {
			const bodyText = isIntake ? `Are you sure you would like to save and submit this ${intakeLabel.toLowerCase()} form? You will be able to resubmit if the form is rejected.` : 'Are you sure you would like to save and submit this assessment?  The assessment will be closed.';
			const confirmed = await confirmDialog({
				dialogTitle: 'Assessment Submit',
				bodyText: bodyText,
				confirmText: 'Submit',
				confirmStyle: 'success',
				cancelText: 'Cancel',
			});
			if (!confirmed) return;
		}

		// Submit the form
		$main.find('#response_form_submit').prop('disabled', true);
		$main.find('#response_form_spinner').show();

		try {
			const postData = {
				type: 'response_save',
				data,
			};
			const res = await ajaxPromise('/form/submit', postData);
			if (res.rc !== 'OK') throw res;

			response.resp_guid = res.guid;
			dataCheckDirty(false);
			if (historyTable) historyTable.ajax.reload();
			displayNotification('Assessment', 'Assessment progress saved.', 'success');
			if (isExternal && taskSubmit) window.location.reload();
			if (isExternal && !taskSubmit) {
				await confirmDialog({
					dialogTitle: 'Assessment Saved',
					bodyText: 'Your progress has been saved.  You can continue editing and submit the assessment when you have finished, or close this window if you have finished but would like to leave the assessment open for others.',
					confirmText: 'Ok',
					confirmStyle: 'primary',
					showCancelButton: false,
				});
			}
			if (!isExternal && needsReview && anyReviewables()) $main.find('#response_form_review').show().on('click', () => locking({ $btn: $main.find('#response_form_review'), status: SURVEY_REVIEW }));
			else $main.find('#response_form_review').off('click').hide();
		} catch (error) {
			displayNotification('Assessment', 'There was an error saving progress.', 'danger');
			logerror('response submit', error);
		}

		$main.find('#response_form_spinner').hide();
		$main.find('#response_form_submit').prop('disabled', false);
	};

	const checkComplete = () => {
		if (currentStatus == SURVEY_COMPLETE) return true;
		if (currentStatus == SURVEY_REVIEW) {
			const incompleteQuestion = $main.find('#response_form [data-questionid]').toArray().find((element: HTMLElement) => {
				const $question = $(element);
				if (+$question.data('hidden')) return false; // Conditional/hidden questions don't count
				const incomplete = $question.find('.answer').data('answered') == 0;
				return incomplete;
			});
			return !incompleteQuestion;
		}
		const incompleteQuestion = $main
			.find('#response_form [data-questionid]')
			.toArray()
			.find((element: HTMLElement) => {
				const $question = $(element);
				if (+$question.data('hidden')) return false; // Conditional/hidden questions don't count

				const $value = $question.find('select.answer_value');
				const value = $value.val() != null ? $value.val() : '';
				const $importance = $question.find('select.answer_importance');
				const importance = $importance.val() != null ? parseFloat($importance.val().toString()) : '';

				if (value == '' && importance == '') return true; //Incomplete question found

				const ans = {
					guid: $question.data('questionid'),
					value,
					text: $value.children(':selected').length == 1 ? $value.children(':selected').text() : '',
					importance,
				};

				// Check the other required fields, assuming value is set
				return ans.guid == '' || ans.text == '' || ans.value == '' || (type == ASSESSMENT_VENDORVALUE && ans.text != 'N/A' && ans.importance == '');
			});

		return !incompleteQuestion;
	};

	const anyReviewables = () => {
		const editedAnswer = $main
			.find('#response_form [data-questionid]')
			.toArray()
			.find((element: HTMLElement) => {
				const $question = $(element);
				if (+$question.data('hidden')) return false; // Conditional/hidden questions don't count

				// Check select dropdown (for regular assessments and intake select questions)
				const $value = $question.find('select.answer_value');
				let value = $value.val() != null ? $value.val() : '';
				if ($question.data('questionid') == CRITICALITY_QUESTION_GUID && $value.find(':selected').text().toLowerCase() === defaultCritical) value = '';
				
				// For intake forms, also check text inputs and textareas
				if (isIntake) {
					const $textValue = $question.find('input.answer_text_value, textarea.answer_text_value');
					const textValue = $textValue.val() ? ($textValue.val() as string).trim() : '';
					if (textValue != '') return true;
				}
				
				const comment = $question.find('textarea.answer_comment').val() || '';
				if (value != '' || comment != '') return true;
			});
		return !!editedAnswer;
	};

	const lock = ({ target }: JQuery.ClickEvent<HTMLElement>) => {
		if (!checkComplete()) {
			displayNotification('Assessment', 'Cannot lock, all available questions must be answered first.', 'danger');
			return;
		}
		locking({ $btn: $(target), status: SURVEY_COMPLETE });
	};

	const dataCheckDirty = (isDirty: boolean) => {
		dirty = isDirty;
		if (isDirty || (isExternal && showSubmit)) {
			$main.find('#response_form_task_submit_external').fadeIn(300);
			$main.find('#response_form_submit').fadeIn(300).off('click').on('click', (event) => {
				event.preventDefault();
				saveProgress();
			});
			$main.find('#response_form_review').off('click').hide();
			$main.find('#response_form_cancel').text(`Cancel & ${response.return_to_text}`);

			$main.find('#response_form_lock').hide().off('click');
		} else {
			// is clean to lock
			if (checkComplete() && !needsReview) {
				$main.find('#response_form_lock').fadeIn(300).off('click').on('click', lock);
			}
			$main.find('#response_form_cancel').text(response.return_to_text);

			$main.find('#response_form').off('submit');
			if (!isExternal) $main.find('#response_form_submit').hide();
		}
	};

	const closeForm = async () => {
		if (dirty) {
			$main.find('#response_form_cancel_modal_confirm').off('click').on('click', () => returnToX());
			$main.find('#response_form_cancel_modal').modal();
			return;
		}

		returnToX();
	};

	const locking = async ({ $btn, status, approve = null }: { $btn: $, status: number, approve?: 0 | 1 }) => {
		const reviewers = $main.find('#response_form_reviewers').val() || [];
		const reviewType = isIntake ? intakeNotify : cat_review;
		
		// Only require manual reviewers if the review type is NOTIFY_MANUAL (not NOTIFY_AUTO or NOTIFY_MANUAL_AND_AUTO)
		if (status == SURVEY_REVIEW && reviewType == NOTIFY_MANUAL && !reviewers?.length) {
			displayNotification('Assessment', 'No reviewers are setup. Please contact your 3rd Party Toolbox Administrator to add reviewers.', 'danger');
			return;
		}
		if (status == SURVEY_REVIEW && dirty) {
			const confirmed = await confirmDialog({
				dialogTitle: 'Assessment Submit',
				bodyText: 'There are unsaved changes. Are you sure you would like to submit this assessment for review? Unsaved changes will be lost.',
				confirmText: 'Submit for Review',
				confirmStyle: 'success',
				cancelText: 'Cancel',
			});
			if (!confirmed) return;
		}
		$btn.prop('disabled', true);

		try {
			const postData = {
				type: 'response_lock',
				data: {
					resp_guid: response.resp_guid,
					status,
					type,
					approve,
					review_msg: $main.find('#response_form_review_message').val(),
					reviewers,
				},
			};
			const res = await ajaxPromise('/form/submit', postData);
			if (res.rc !== 'OK') throw res;
			if (isInline) {
				returnToX();
			} else {
				post('/ui/assessment', {
					surv_guid: response.resp_guid,
					vend_id: vendId,
					cat_id: response.cat_id,
					type,
					review: response.review,
					intake_hash: intakeHash,
				});
			}
		} catch (error) {
			switch (error.rc) {
				case 'NO_BASIS':
					displayNotification('Assessment', 'All answers cannot be N/A.', 'danger');
					break;
				default:
					displayNotification('Assessment', 'There was an error submitting this assessment.', 'danger');
					logerror('response lock', error);
			}
		}

		$btn.prop('disabled', false);
	};

	const returnToX = () => {
		$main.find('#response_form :input').val('');
		if ([ASSESSMENT_CONCHECKLIST, ASSESSMENT_CONTRACT].includes(type)) {
			post('/ui/contract', {
				vend_id: vendId,
				contract_guid: response.con_guid,
				start: type === ASSESSMENT_CONCHECKLIST ? 'checklist' : 'contract_risk',
			});
			return;
		}
		if (type === ASSESSMENT_INTAKE) {
			window.location.href = `/ui/${intakeRouteSlug}`;
			return;
		}
		loadVendorEdit({ vend_id: +vendId, assessType: type });
	};

	const checkQuestionDisplay = () => {
		const getHiddenState = () => {
			const state = {};
			$main.find('#response_form [data-questionid]').each((_, element) => {
				const $question = $(element);
				state[$question.data('questionid')] = $question.data('hidden');
			});
			return state;
		};
		const originalState = getHiddenState();
		$main.find('#response_form [data-questionid]').each((_, element) => {
			const $question = $(element);
			originalState[$question.data('questionid')] = $question.data('hidden');
		});
		$main.find('#response_form [data-hidden]').hide().data('hidden', 1);
		$main.find('#response_form select').each((_, select) => {
			const $select = $(select);

			// Show targets that are selected
			if ($select.children('option:selected').data('target')) {
				const ref = $select.children(':selected').data('target').split(',');
				ref.forEach((target) => {
					const $target = $main.find("#response_form [data-questionid='" + target + "']");
					if ($target.length == 1) {
						$target.data('hidden', 0);
						$target.show();
					}
				});
			}
		});
		const newState = getHiddenState();
		const changedSections = [];
		Object.keys(newState).forEach((key) => {
			if (originalState[key] != newState[key]) {
				const section = $main.find(`#response_form [data-questionid="${key}"]`).closest('section')[0];
				if (!changedSections.includes(section)) changedSections.push(section);
			}
		});
		changedSections.forEach((section) => checkSectionScoring($(section), type));
	};

	// Assessment Response
	let dirty = false;

	let response = JSON.parse(atob($main.find('.response_form_container').data('params')));

	const isExternal = response.is_external;
	const isInline = response.is_inline;
	const showSubmit = response.show_submit;
	const isIntake = !!+response.is_intake;
	const type = +response.type;
	const currentStatus = +response.status;
	const cat_review = +response.cat_review;
	const intakeNotifyEnabled = !!+response.intake_notify_enabled;
	const intakeNotify = +response.intake_notify || NOTIFY_NONE; // Actual intake_notify value (NOTIFY_NONE, NOTIFY_MANUAL, NOTIFY_AUTO, NOTIFY_MANUAL_AND_AUTO)
	const isReviewer = !!+response.is_reviewer;
	const vendId = response?.vend_id;
	const defaultCritical = response.default_critical;
	const lockHeartbeatMinutes = response.lock_heartbeat_minutes;
	const lockTimeoutMinutes = response.lock_timeout_minutes;
	const lockingEnabled = Number(response.lock_record_enabled); // global locking option enabled flag
	const intakeHash = response.intake_hash;
	const intakeRouteSlug = response.intake_route_slug || 'vendor_intake';
	const intakeLabel = response.intake_label || 'Vendor Intake';
	delete response.is_external;
	delete response.show_submit;
	delete response.cat_review;
	delete response.intake_notify_enabled;
	delete response.intake_notify;
	delete response.status;
	delete response.is_reviewer;
	delete response.default_critical;
	delete response.intake_hash;
	delete response.intake_label;
	// For intake forms, use intake_notify_enabled; for others, use cat_review
	const reviewEnabled = isIntake ? intakeNotifyEnabled : cat_review;
	const needsReview = reviewEnabled && !isReviewer && currentStatus != SURVEY_COMPLETE;

	// Update URL with intake hash without reloading the page
	if (isIntake && intakeHash) {
		const currentPath = window.location.pathname;
		// Use the configured route slug (vendor_intake uses 'intake', outsource_planning uses 'planning')
		const routePath = intakeRouteSlug === 'outsource_planning' ? 'planning' : 'intake';
		const expectedPath = `/ui/${routePath}/${intakeHash}`;
		
		// Only update if the current URL doesn't already have the hash
		if (currentPath !== expectedPath && !currentPath.includes(intakeHash)) {
			window.history.replaceState(null, '', expectedPath);
		}
	}

	let recordLock: RecordLock | null = null;

	historyTable = historyTables.get(`history_table_${type}`);

	if ((currentStatus === SURVEY_OPEN || currentStatus === SURVEY_COMPLETE) && 
	!!lockingEnabled && !isInline && !isExternal && !isIntake) { // global locking option enabled, not inline, not external, not intake 
		const contractTypes = [ASSESSMENT_CONCHECKLIST, ASSESSMENT_CONTRACT];
		recordLock = new RecordLock({
			lockType: ASSESSMENT_TO_LOCK_TYPE[type],
			lockHeartbeatMinutes,
			lockTimeoutMinutes: lockTimeoutMinutes,
			options: { assessment_status: currentStatus },
			recordId: contractTypes.includes(type) ? response.con_guid : vendId, // vendor id for assessments
			bannerLocation: $main.find('.response_form_container'),
			bannerRefreshSeconds: response.lock_banner_refresh_seconds,
			toggle: (disabled: boolean) => toggleAssessmentEditing($main, disabled),
			onInactivitySave: () => saveProgressBackground($main, response, historyTable),
			onInactivityNavigation: () => returnToX(),
			onRefresh: () => refreshAssessmentData({ $main, response }), // Refresh data when lock is acquired
		});

		await recordLock.startLock();
	}

	response.answers = [];

	if (vendId) {
		$('#task_add_modal').on('show.bs.modal', () => {
			$('#task_add_modal').find('#task_add_vendor').val([vendId]).trigger('chosen:updated');
		});
	}

	$(document).on('scroll', () => {
		if ($(document).scrollTop() > 160) $main.find('#scroll-top').show();
		else $main.find('#scroll-top').hide();
	});

	$main.find('#response_export').off('click').on('click', () => {
		post('/export/response', { surveyresp_guid: response.resp_guid }, '_blank');
	});

	$main.find('#response_print').off('click').on('click', () => {
		post('/printout/assessment', { surveyresp_guid: response.resp_guid }, '_blank');
	});

	if (currentStatus === SURVEY_REVIEW) {
		const isComplete = checkComplete();
		$main.find('#response_form_review_approve').text(isComplete ? 'Approve & Mark Complete' : 'Approve & Continue Editing').off('click').on('click', ({ target }) => {
			locking({ $btn: $(target), status: isComplete ? SURVEY_COMPLETE : SURVEY_OPEN, approve: 1 });
		});
	}

	$main.find('#response_form_review_reject').off('click').on('click', ({ target }) => {
		locking({ $btn: $(target), status: SURVEY_OPEN, approve: 0 });
	});

	$main.find('#scroll-top-btn').off('click').on('click', () => $(window).scrollTop(0));

	$main.find('#response_form .answer_importance_tooltip').popover({
		html: true,
		content: $main.find('#importance_tooltip_content').html(),
		trigger: 'hover focus',
	});

	$main.find('.response_form_question_add').on('click', ({ target }) => {
		const $section = $(target).parent();
		const $clone = $main.find('#response_form_question_template > div').clone(true);
		$section.find('p.noquestions').remove();
		$section.append($clone).show();
		return false;
	});

	$main.find('.response_form_question_remove').on('click', ({ target }) => {
		$(target).closest('.response_form_question_custom').remove();
		return false;
	});

	if (response.resp_id !== null && response.resp_guid !== null) {
		// In-progress Assessment
		checkQuestionDisplay();

		if (checkComplete() && !needsReview) {
			$main.find('#response_form_lock').show().on('click', lock);
		}
		
		// For intake forms, show review button if needsReview is true (don't require anyReviewables check)
		// For other forms, require both needsReview and anyReviewables
		if (needsReview && (isIntake || anyReviewables())) {
			$main.find('#response_form_review').show().on('click', () => locking({ $btn: $main.find('#response_form_review'), status: SURVEY_REVIEW }));
		}

		$main.find('#response_form select.answer_value').chosen({ width: '100%' });
		$main.find('#response_form select.answer_value').on('change', async ({ target }) => {
			const $answer = $(target);
			const $section = $answer.closest('section');
			checkSectionScoring($section, type);
		});
	}

	$main.find('#response_form_reviewers').chosen({ width: '100%' });
	if ($main.find('#intake_reviewers').length) {
		$main.find('#intake_reviewers').chosen({ width: '100%' });
	}

	// Completed Assessment
	$main.find('#response_form .answer').each((_, element) => {
		const $answer = $(element);
		if (!$answer.data('target')) return;
		const ref = JSON.parse(atob($answer.data('target')));
		ref.forEach((target) => {
			const $target = $main.find("#response_form [data-questionid='" + target + "']");
			if ($target.length == 1) {
				$target.data('hidden', 0);
				$target.show();
			}
		});
	});

	$main.find('#response_form_task_submit_external').off('click').on('click', (event) => {
		event.preventDefault();
		saveProgress({ taskSubmit: true });
	});

	$main.find('#response_form :input').on('change', () => {
		checkQuestionDisplay();
		dataCheckDirty(true);
	});

	$main.find('#response_form_reviewedby, #response_form_date').on('change', () => dataCheckDirty(true));
	$main.find('#response_form_question_template :input').on('change', () => dataCheckDirty(true));
	$main.find('#intake_vendor_name').on('change', () => dataCheckDirty(true));

	if ($main.find('#response_form_unlock').length == 1) {
		$main.find('#response_form_unlock').off('click').on('click', ({ target }) => locking({ $btn: $(target), status: SURVEY_OPEN }));
		if ($main.find('#response_form_override_update').length == 1) {
			// Disable override reason if override is empty
			const overrideIsEmpty = $main.find('#response_form_override').val() == '';
			$main.find('#response_form_override_reason').prop('disabled', overrideIsEmpty);

			// Disable override reason if override is empty, also on change
			$main.find('#response_form_override').off('change').on('change', ({ target }) => {
				if ($(target).val() == '') {
					$main.find('#response_form_override_reason').val('');
					$main.find('#response_form_override_reason').prop('disabled', true);
				} else {
					$main.find('#response_form_override_reason').prop('disabled', false);
				}
			});

			$main.find('#response_form_override_update').off('click').on('click', async () => {
				const overrideId = $main.find('#response_form_override').val();
				const overrideTitle = $main.find('#response_form_override > option:selected').text();
				const reason = $main.find('#response_form_override_reason').val();

				if (overrideId != '' && reason == '') {
					displayNotification('Override', 'Please provide a reason for the override.', 'danger');
					return;
				}

				try {
					const postData = {
						type: 'response_override',
						data: {
							resp_guid: response.resp_guid,
							override: overrideId,
							reason,
						},
					};
					const res = await ajaxPromise('/form/submit', postData);
					if (res.rc !== 'OK') throw res;

					displayNotification('Override', 'Override set.', 'success');
					const rating = $main.find('#response_rating').data('rating');
					if (overrideId == '' || overrideId == null) {
						$main.find('#response_rating').html(htmlEsc(rating));
					} else {
						$main.find('#response_rating').html(htmlEsc(`${overrideTitle} (scored ${rating})`));
					}
					if (historyTable) historyTable.ajax.reload();
				} catch (error) {
					displayNotification('Override', 'There was an error setting the override.', 'danger');
					logerror('response override submit', error);
				}
			});
		}
	}

	$main.find('#response_form_cancel').off('click').on('click', () => closeForm());

	if ($main.find('#response_form_delete').length) {
		$main.find('#response_form_delete').off('click').on('click', async ({ target }) => {
			const confirmed = await confirmDialog({
				dialogTitle: 'Assessment Delete',
				bodyText: 'Are you sure you would like to delete this assessment?',
				confirmText: 'Delete',
				confirmStyle: 'danger',
				cancelText: 'Cancel',
			});
			if (!confirmed) return;

			const $btn = $(target);
			$btn.prop('disabled', true);

			try {
				const postData = {
					type: 'response_delete',
					data: {
						guid: response.resp_guid,
					},
				};
				const res = await ajaxPromise('/form/submit', postData);
				if (res.rc !== 'OK') throw res;

				returnToX();
			} catch (error) {
				displayNotification('Assessment', 'There was an error deleting this assessment.', 'danger');
				logerror('response delete', error);
			}

			$btn.prop('disabled', false);
		});
	}

	if (isExternal && showSubmit) dataCheckDirty(false);

};

$('.response_form_outer_container').each((_, element) => { initResponse($(element)); });

if ($('#assessment_print_container').length == 1) {
	setTimeout(() => window.print(), 1000);
}
